import React, { useLayoutEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Pencil, X } from "lucide-react";
import { patientPhotoUrl } from "../../config/practice";
import { categoryOf, isCancelled, prettyType } from "../apptTypes";
import "./aptcard.css";

const WIDTH = 360;
const GAP = 12;

// Status pill tone: in the building green, finished navy, cancelled red.
const statusTone = (s) => {
  const v = String(s || "").toLowerCase();
  if (!v) return "none";
  if (/cancel|no[\s-]?show/.test(v)) return "off";
  if (/checked out/.test(v)) return "done";
  if (/ready|processing|checked in|in-office|with/.test(v)) return "on";
  return "neutral";
};

/* Appointment card: opens beside the clicked appointment when `anchor` (its
   DOMRect) is given, otherwise centred over a dimmed page (the dashboard).
   Every action is as before: name / photo open the patient on the dashboard,
   Status, Room, Provider and Type open their editors, Open appointment /
   Reschedule and Take payment. */
export default function AptCard({ setIsAptcard, setScreen, selectedAppointment, isfutureappointment, anchor, lockCancelled = false }) {
  const homescreenprev = useSelector((state) => state.userPrivileges.homescreen) || [];
  const navigate = useNavigate();
  const boxRef = useRef(null);
  const [pos, setPos] = useState(null);
  const a = selectedAppointment || {};

  useLayoutEffect(() => {
    if (!anchor || !boxRef.current) return;
    const h = boxRef.current.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // the drawn width: CSS caps it at min(360px, 100vw - 24px) on phones
    const w = boxRef.current.offsetWidth || WIDTH;
    let left = anchor.right + GAP;
    if (left + w > vw - GAP) left = anchor.left - GAP - w;
    if (left < GAP) left = Math.max(GAP, Math.min(vw - w - GAP, anchor.left));
    const top = Math.max(GAP, Math.min(vh - h - GAP, anchor.top - 8));
    setPos({ left, top });
  }, [anchor, a.appointmentid]);

  const close = () => setIsAptcard(false);
  const openPatient = () => navigate("/landing", { state: { fromPatientId: a.patientid } });
  /* The scheduler draws cancelled / no-show visits (struck through); from
     there the card is read-only: a status, provider, type or room edit on a
     cancelled row is how Cancelled visits got resurrected. Open appointment
     stays (it lists the patient's visits, and re-booking starts there). */
  const locked = lockCancelled && isCancelled(a);
  const canStatus = !locked && homescreenprev.includes("Visit Confirmation");
  const canRoom = !locked && homescreenprev.includes("Room Assignment");
  const cat = categoryOf(a.type);
  const when = a.startdate
    ? `${dayjs(a.startdate).format("ddd, MMM D")} · ${dayjs(`2000-01-01 ${a.starttime}`).format("h:mm A")}`
    : dayjs(`2000-01-01 ${a.starttime}`).format("h:mm A");
  const dob = a.dob && dayjs(a.dob).isValid() ? dayjs(a.dob).format("MM/DD/YYYY") : a.dob;

  const Row = ({ k, children, onClick, title }) => (
    <div className="bmapt-row">
      <span className="bmapt-k">{k}</span>
      {onClick ? (
        <button type="button" className="bmapt-v edit" onClick={onClick} title={title}>
          {children}
          <Pencil aria-hidden="true" />
        </button>
      ) : (
        <span className="bmapt-v">{children}</span>
      )}
    </div>
  );

  return (
    <div className={`bmapt-layer${anchor ? "" : " dim"}`} onClick={close}>
      <div
        ref={boxRef}
        className={`bmapt${anchor ? " anchored" : ""}`}
        style={anchor ? (pos ? { left: pos.left, top: pos.top } : { visibility: "hidden" }) : undefined}
        role="dialog"
        aria-label={`${a.firstname || ""} ${a.lastname || ""} appointment`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bmapt-head" style={{ "--c": cat.color }}>
          <button type="button" className="bmapt-photo" onClick={openPatient} title="Open on the dashboard">
            <img
              src={patientPhotoUrl(a.patientid)}
              alt=""
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = "data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==";
              }}
            />
          </button>
          <div className="bmapt-who">
            <button type="button" className="bmapt-name" onClick={openPatient} title="Open on the dashboard">
              {a.firstname} {a.lastname}
            </button>
            <span className="bmapt-sub">
              #{a.patientid}
              {dob ? ` · DOB ${dob}` : ""}
            </span>
          </div>
          <button type="button" className="bmapt-x" onClick={close} aria-label="Close">
            <X />
          </button>
        </div>

        <div className="bmapt-body">
          <Row k="When">{when}</Row>
          <Row k="Provider" onClick={locked ? null : () => setScreen(5)} title="Change provider">
            {a.providername || "—"}
          </Row>
          <Row k="Type" onClick={locked ? null : () => setScreen(7)} title="Change visit type">
            <span className="bmapt-pill" style={{ "--c": cat.color, "--t": cat.tint }}>
              {prettyType(a.type) || cat.label}
            </span>
          </Row>
          <Row k="Status" onClick={canStatus ? () => setScreen(2) : null} title="Update visit status">
            <span className={`bmapt-status ${statusTone(a.visit_confirmation)}`}>
              {a.visit_confirmation || "No status"}
            </span>
          </Row>
          <Row k="Room" onClick={canRoom ? () => setScreen(6) : null} title="Change room">
            {a.room_no || "OP"}
          </Row>
          {a.phone && <Row k="Phone">{a.phone}</Row>}
          {a.insurancename && <Row k="Insurance">{a.insurancename}</Row>}
          {a.notes && <Row k="Notes">{a.notes}</Row>}
        </div>

        <div className="bmapt-foot">
          <button type="button" className="bmapt-btn primary" onClick={() => setScreen(1)}>
            {!isfutureappointment ? "Open appointment" : "Reschedule"}
          </button>
          {!isfutureappointment && !locked && (
            <button type="button" className="bmapt-btn" onClick={() => setScreen(3)}>
              Take payment
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
